import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'antd';
import PropTypes from 'prop-types';

import CustomConfirmModal from '../../../../common/modals/CustomConfirmModal';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import AddCostCodeDropdown from '../AddCostCodeDropdown';
import BudgetTrackingCollapse from '../BudgetTrackingCollapse';
import BudgetTrackingCostCodesTable from '../BudgetTrackingCostCodesTable';
import BudgetTrackingPhasesTable from '../BudgetTrackingPhasesTable';
import CostTrackingBudgetProgress from './CostTrackingBudgetProgress';
import Permissions from '../../../../auth/Permissions';
import useToggle from '../../../../common/hooks/useToggle';
import ProjectAddPhaseCodeDrawer from '../../ProjectAddPhaseCodeDrawer';
import { formatPayloadCustomData } from '../../../../equipment/state/equipment.service';
import { updateProjectFromCostTracking } from '../../../state/projects.actions';
import { enrichRowsWithCostUpdates } from '../../../ProjectProgress/progressHelpers';

const tabs = [
  { key: 'progress', title: 'Progress' },
  { key: 'costcodes', title: 'Cost Codes' },
  { key: 'phases', title: 'Phases' },
];

const cleanAssignmentFields = (categoryAssignments) => {
  if (!categoryAssignments || typeof categoryAssignments !== 'object') return {};
  const newAssignments = {};
  Object.entries(categoryAssignments).forEach(([catId, a]) => {
    if (!a || typeof a !== 'object') return;
    newAssignments[catId] = { estimatedCost: a.estimatedCost, hours: a.hours, days: a.days };
  });
  return newAssignments;
};

export default function CostTrackingBudget({
  divisionId,
  costcodesTableData,
  phases,
  categorizedBudgetUpdateMap,
  onUploadInvoice,
  onHistoryClick,
  projectId,
  averageWage = 0,
  useBuildEstimate = false,
  projectCostcodes,
  onCostcodesChanged,
}) {
  const dispatch = useDispatch();

  const hasWrite = Permissions.has('PROJECT_PROGRESS_WRITE');
  const categories = useSelector((state) => state.costcodes.categories);

  const customCategories = useMemo(
    () => Object.values(categories || {}).filter((c) => !c?.isDefault),
    [categories],
  );

  const [selectedTab, setSelectedTab] = useState('progress');
  const [selectedPhase, setSelectedPhase] = useState({});
  const [selectedPhaseCostcodes, setSelectedPhaseCostcodes] = useState([]);

  const {
    isToggled: showPhaseDrawer,
    toggle: togglePhaseDrawer,
  } = useToggle();

  const unphasedData = useMemo(() => (
    costcodesTableData.filter((cc) => !cc.phased)
  ), [costcodesTableData]);

  const budgetUnphasedData = useMemo(() => (
    enrichRowsWithCostUpdates(unphasedData, categorizedBudgetUpdateMap)
  ), [unphasedData, categorizedBudgetUpdateMap]);

  const budgetPhases = useMemo(() => (
    (phases || []).map((phase) => ({
      ...phase,
      costcodes: enrichRowsWithCostUpdates(phase.costcodes ?? [], categorizedBudgetUpdateMap),
    }))
  ), [phases, categorizedBudgetUpdateMap]);

  const applyCostTrackingUpdate = useCallback(async (payload) => {
    const success = await dispatch(updateProjectFromCostTracking(projectId, payload));
    if (success) onCostcodesChanged?.();
    return success;
  }, [dispatch, projectId, onCostcodesChanged]);

  const onCostcodeAdd = useCallback(async (costcodesToAdd) => {
    if (!costcodesToAdd?.length || !projectId || !hasWrite) return;
    const isFromGlobal = costcodesToAdd.every(
      (c) => c.id && typeof c.id === 'string' && c.id.length === 36,
    );
    const payload = isFromGlobal
      ? { fromGlobalCostcodes: costcodesToAdd }
      : {
        createdCostcodes: await Promise.all(
          costcodesToAdd.map(async (cc) => {
            const { customData, files } = await formatPayloadCustomData(cc.customData);
            const mergedFiles = cc.files ? [...(cc.files || []), ...files] : files;
            return { ...cc, customData, files: mergedFiles };
          }),
        ),
      };
    await applyCostTrackingUpdate(payload);
  }, [projectId, hasWrite, applyCostTrackingUpdate]);

  const onEditCostcode = useCallback(async (record, updateObj) => { // unphased costcode
    if (!record?.id || !projectId || !hasWrite || !updateObj) return;
    const categoryAssignments = cleanAssignmentFields(updateObj.categoryAssignments);
    if (Object.keys(categoryAssignments).length === 0
      && updateObj.hourlyWage === undefined
      && updateObj.dailyWage === undefined) return;
    const payload = {
      modifiedCostcodeEstimates: {
        costcodeId: record.id,
        phaseId: null,
        categoryAssignments,
        ...(updateObj.hourlyWage !== undefined && { hourlyWage: updateObj.hourlyWage }),
        ...(updateObj.dailyWage !== undefined && { dailyWage: updateObj.dailyWage }),
      },
    };
    await applyCostTrackingUpdate(payload);
  }, [projectId, hasWrite, applyCostTrackingUpdate]);

  const onEditPhaseCostCodes = useCallback(async (
    phaseId,
    updatedPhaseCostcodes,
    updatedCostcodeId,
  ) => {
    if (!phaseId || !updatedPhaseCostcodes?.length || !projectId || !hasWrite) return;
    const phase = phases.find((p) => p.id === phaseId);
    if (!phase) return;
    const edited = updatedPhaseCostcodes.find((uc) => uc.id === updatedCostcodeId);
    if (!edited) return;
    const categoryAssignments = cleanAssignmentFields(edited.categoryAssignments);
    const payload = {
      modifiedCostcodeEstimates: {
        costcodeId: edited.id,
        phaseId,
        categoryAssignments,
        ...(edited.hourlyWage !== undefined && { hourlyWage: edited.hourlyWage }),
        ...(edited.dailyWage !== undefined && { dailyWage: edited.dailyWage }),
      },
    };
    await applyCostTrackingUpdate(payload);
  }, [projectId, hasWrite, phases, applyCostTrackingUpdate]);

  const onSelectPhase = useCallback((index) => {
    const newSelectedPhase = phases?.[index - 1] ?? {};
    setSelectedPhase(newSelectedPhase);
    setSelectedPhaseCostcodes(newSelectedPhase.costcodes ?? []);
    togglePhaseDrawer();
  }, [phases, togglePhaseDrawer]);

  const onRemovePhase = useCallback((phase) => {
    if (!phase?.id || !projectId || !hasWrite) return;
    CustomConfirmModal({
      title: 'Remove phase',
      content: `Remove phase "${phase.name}"? This will remove the phase and all cost codes in it. Cost codes that are only in this phase will be removed from the project.`,
      okText: 'Remove',
      cancelText: 'Cancel',
      onOk: async () => {
        await applyCostTrackingUpdate({ removedPhaseId: phase.id });
      },
    });
  }, [projectId, hasWrite, applyCostTrackingUpdate]);

  const onSubmitPhase = useCallback(async (values, submitted) => {
    if (!submitted || !values || !projectId || !hasWrite) {
      togglePhaseDrawer();
      return true;
    }

    const isEdit = !!selectedPhase?.id;

    const customCategoryEstimates = customCategories.map((cat) => ({
      categoryId: cat.id,
      value: values?.[`phaseEstimate.${cat.id}`] ?? 0,
    }));

    const payload = {};

    if (isEdit) {
      const previousCostcodeIds = new Set((selectedPhase.costcodes ?? []).map((c) => c.id));
      const currentCostcodeIds = new Set((selectedPhaseCostcodes || []).map((c) => c.id));
      const removePhaseLinks = (selectedPhase.costcodes ?? [])
        .filter((c) => !currentCostcodeIds.has(c.id))
        .map((c) => ({ phaseId: selectedPhase.id, costcodeId: c.id }));
      const createdPhaseLinks = (selectedPhaseCostcodes || [])
        .filter((c) => !previousCostcodeIds.has(c.id))
        .map((c) => ({
          phaseId: selectedPhase.id,
          costcodeId: c.id,
          categoryAssignments: c.categoryAssignments ?? {},
        }));

      payload.modifiedPhase = {
        phaseId: selectedPhase.id,
        name: values.phaseName,
        description: values.description,
        estimatedLabourHours: values.estimatedLabourHours,
        estimatedLabourCost: values.estimatedLabourCost,
        estimatedOverheadHours: values.estimatedOverheadHours,
        estimatedMaterialCost: values.estimatedMaterialCost,
        estimatedOverheadCost: values.estimatedOverheadCost,
        estimatedEquipmentCost: values.estimatedEquipmentCost,
        estimatedEquipmentHours: values.estimatedEquipmentHours,
        customCategoryEstimates,
        createdPhaseLinks,
        removePhaseLinks,
      };
    } else {
      payload.newPhase = {
        name: values.phaseName,
        description: values.description,
        estimatedLabourHours: values.estimatedLabourHours,
        estimatedLabourCost: values.estimatedLabourCost,
        estimatedOverheadHours: values.estimatedOverheadHours,
        estimatedMaterialCost: values.estimatedMaterialCost,
        estimatedOverheadCost: values.estimatedOverheadCost,
        estimatedEquipmentCost: values.estimatedEquipmentCost,
        estimatedEquipmentHours: values.estimatedEquipmentHours,
        customCategoryEstimates,
        costcodes: (selectedPhaseCostcodes || []).map((cc) => ({
          id: cc.id,
          categoryAssignments: cc.categoryAssignments ?? {},
        })),
      };
    }

    const ok = await applyCostTrackingUpdate(payload);
    togglePhaseDrawer();
    return !!ok;
  }, [
    projectId,
    hasWrite,
    selectedPhase,
    selectedPhaseCostcodes,
    customCategories,
    togglePhaseDrawer,
    applyCostTrackingUpdate,
  ]);

  const onRemoveUnphasedCostcode = useCallback(async (costcode) => {
    if (!costcode?.id || !projectId || !hasWrite) return;
    await applyCostTrackingUpdate({ removedUnphasedCostcodeIds: [costcode.id] });
  }, [projectId, hasWrite, applyCostTrackingUpdate]);

  return (
    <div id="budget">
      <BudgetTrackingCollapse
        panelKey="budget"
        header={<span className="budget-tracking-section-title">Budget & Actual Costs</span>}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: 16, marginTop: 24 }}>
          <Col>
            <div className="cost-tracking-budget-tabs">
              {tabs.map((tab) => (
                <OnTraccrButton
                  key={tab.key}
                  className={selectedTab === tab.key ? 'cost-tracking-budget-tab-selected' : 'cost-tracking-budget-tab'}
                  onClick={() => setSelectedTab(tab.key)}
                  title={tab.title}
                />
              ))}
            </div>
          </Col>
          {hasWrite && (
            <Col>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <OnTraccrButton
                  title="Add Phase"
                  style={{ height: 40 }}
                  onClick={() => {
                    setSelectedPhase({});
                    setSelectedPhaseCostcodes([]);
                    togglePhaseDrawer();
                  }}
                />
                <AddCostCodeDropdown
                  divisionId={divisionId}
                  chosen={projectCostcodes}
                  onCostcodeAdd={onCostcodeAdd}
                  isTemplate
                />
              </div>
            </Col>
          )}
        </Row>
        <div>
          {selectedTab === 'progress' && (
            <CostTrackingBudgetProgress
              unphasedData={unphasedData}
              phases={phases}
              onUploadInvoice={onUploadInvoice}
              onHistoryClick={onHistoryClick}
              projectId={projectId}
              averageWage={averageWage}
            />
          )}
          {selectedTab === 'costcodes' && (
            <BudgetTrackingCostCodesTable
              expandedData={budgetUnphasedData}
              phases={budgetPhases}
              isNotDisplay={false}
              onRemoveCostcode={hasWrite ? onRemoveUnphasedCostcode : null}
              showEditButton={hasWrite}
              onEditCostcode={hasWrite ? onEditCostcode : undefined}
              useBuildEstimate={useBuildEstimate}
            />
          )}
          {selectedTab === 'phases' && (
            <div className="cost-tracking-budget-phases">
              <BudgetTrackingPhasesTable
                dataSource={budgetPhases}
                showEditButton={hasWrite}
                showDeleteButton={hasWrite}
                isNotDisplay={false}
                openAddDrawer={onSelectPhase}
                onRemove={onRemovePhase}
                useBuildEstimate={useBuildEstimate}
                onEditPhaseCostCodes={hasWrite ? onEditPhaseCostCodes : undefined}
                isExpanded
              />
            </div>
          )}
        </div>
      </BudgetTrackingCollapse>
      <ProjectAddPhaseCodeDrawer
        onDrawerClosed={onSubmitPhase}
        onSelectedCostCodesChanged={(values) => setSelectedPhaseCostcodes(values)}
        visible={showPhaseDrawer}
        selectedPhase={selectedPhase}
        projectCostcodes={projectCostcodes}
        useRedesign
      />
    </div>
  );
}

const tableRowShape = PropTypes.shape({ rowKey: PropTypes.string });
const phaseShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  description: PropTypes.string,
  costcodes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
});
CostTrackingBudget.propTypes = {
  divisionId: PropTypes.string,
  costcodesTableData: PropTypes.arrayOf(tableRowShape),
  phases: PropTypes.arrayOf(phaseShape),
  categorizedBudgetUpdateMap: PropTypes.shape({}),
  onUploadInvoice: PropTypes.func,
  onHistoryClick: PropTypes.func,
  projectId: PropTypes.string,
  averageWage: PropTypes.number,
  useBuildEstimate: PropTypes.bool,
  projectCostcodes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  onCostcodesChanged: PropTypes.func,
};

CostTrackingBudget.defaultProps = {
  divisionId: null,
  costcodesTableData: [],
  phases: [],
  categorizedBudgetUpdateMap: {},
  onUploadInvoice: null,
  onHistoryClick: null,
  projectId: null,
  averageWage: 0,
  useBuildEstimate: false,
  projectCostcodes: [],
  onCostcodesChanged: null,
};
